import { paths as CustomerCommunicationPaths } from '@luxuryescapes/contract-customer-communication';

import { request } from '~/services/common';

import { makeCustomerCommunicationV1URL } from './customerCommunicationRequestUtils';
import { mapHeroPlannerSchedulesListItem } from './requestGetHeroPlannerSchedulesList';

type HeroPlannerStateSchedulesGetContract =
  CustomerCommunicationPaths['/api/customer-communication/v1/todays-escapes-schedule/hero-planner/schedules/country-groups/{countryGroup}/countries/{country}/states/{state}']['get'];

// the per-state endpoint answers "no rows" with a 404
const NO_ROWS_STATUS = 404;

/**
 * Hero planner rows for a single country and state. Unlike the country group list, a state without rows resolves to an
 * empty list rather than an error, so walking every state doesn't report each empty one to Sentry.
 */
async function requestGetHeroPlannerStateSchedulesList(
  brand: string,
  /**
   * YYYY-MM-DD
   */
  startDate: string,
  /**
   * YYYY-MM-DD
   */
  endDate: string,
  countryGroupId: string,
  countryId: string,
  countryStateId: string,
  signal?: AbortSignal,
): Promise<Array<CustomerCommunication.HeroPlannerSchedulesListItem>> {
  const url = makeCustomerCommunicationV1URL(
    `/todays-escapes-schedule/hero-planner/schedules/country-groups/${countryGroupId}/countries/${countryId}/states/${countryStateId}`,
  );
  url.searchParams.set('brand', brand);
  url.searchParams.set('startDate', startDate);
  url.searchParams.set('endDate', endDate);

  const response: Response = await request(url.toString(), {
    method: 'GET',
    credentials: 'include',
    returnResponse: true,
    allowNonOkResponse: true,
    signal,
  });

  if (response.status === NO_ROWS_STATUS) {
    return [];
  }

  const body: HeroPlannerStateSchedulesGetContract['responses']['200']['content']['application/json'] =
    await response.json();
  if (!response.ok) {
    throw new Error(
      (body as { message?: string })?.message || `Request failed with status ${response.status} for ${countryStateId}`,
    );
  }

  return body.result.map(mapHeroPlannerSchedulesListItem);
}

export default requestGetHeroPlannerStateSchedulesList;
